/**
 * Region resolution.
 *
 * A region is identified by the request domain. The CMS is the source of truth - this
 * table is only a bootstrap fallback so the site still renders if /website/regions is
 * unreachable, and so local development has something to work with.
 *
 * Adding a region in production means adding it in Strapi and pointing the domain at
 * the cluster. It must not require a change to this file.
 */
import type { TrackingCode } from './tags';

export interface RegionLocale { code: string; name: string }

export interface Region {
  code: string;
  name: string;
  domain: string;
  extraDomains?: string[];
  defaultLocale: string;
  locales: RegionLocale[];
  /**
   * This market's analytics and marketing tags, from the CMS. Optional here on purpose:
   * the fallback table below must not carry any, because the fallback is what a broken
   * CMS connection produces and that is not the moment to start loading third-party
   * scripts off a hardcoded list.
   */
  trackingCodes?: TrackingCode[];
  /**
   * Whether this market can take newsletter sign-ups - it has a provider configured and
   * the server holds the credential for it. Optional for the same reason as
   * `trackingCodes`: the fallback table below carries none, so a site running without the
   * CMS draws no sign-up box rather than one that posts into nothing.
   *
   * Deliberately a boolean. Which list an address joins is the server's business.
   */
  newsletterEnabled?: boolean;
}

const regionNames = new Map<string, string>();

/**
 * A market's name in the language the page is in - "Czechy" on a Polish page, "Czechia" on
 * an English one, "Česko" on a Czech one.
 *
 * `Region.name` is one string, written in Polish, so on every other language the switcher
 * and the footer named the markets in a language the page was not in. A region's `code` is
 * its country's ISO 3166 code (`pl`, `cz`, `ro`, `hu`, `sk`), which is exactly what the
 * browser's own `Intl.DisplayNames` is keyed on, so there is no table of translated names
 * to keep and a language added to a market needs nothing here.
 *
 * `Region.name` stays as the fallback - for a code that is not a country, or a browser
 * that cannot name it - so a market added in the CMS never shows up blank.
 */
export function regionName(region: Pick<Region, 'code' | 'name'>, locale: string): string {
  const key = `${locale}:${region.code}`;
  const known = regionNames.get(key);
  if (known !== undefined) return known;

  let name = region.name;
  try {
    const code = region.code.toUpperCase();
    const shown = new Intl.DisplayNames([locale], { type: 'region' }).of(code);
    // `of` hands the code back when it has nothing to say about it.
    if (shown && shown !== code) name = shown;
  } catch {
    // An invalid code or locale throws a RangeError; the CMS's own name is the answer.
  }
  regionNames.set(key, name);
  return name;
}

export const FALLBACK_REGIONS: Region[] = [
  { code: 'pl', name: 'Polska',  domain: 'wecotravel.pl', defaultLocale: 'pl', locales: [{ code: 'pl', name: 'Polski' }, { code: 'en', name: 'English' }] },
  { code: 'cz', name: 'Czechy',  domain: 'wecotravel.cz', defaultLocale: 'cs', locales: [{ code: 'cs', name: 'Čeština' }, { code: 'en', name: 'English' }] },
  { code: 'ro', name: 'Rumunia', domain: 'wecotravel.ro', defaultLocale: 'ro', locales: [{ code: 'ro', name: 'Română' }, { code: 'en', name: 'English' }] },
  { code: 'hu', name: 'Węgry',   domain: 'wecotravel.hu', defaultLocale: 'hu', locales: [{ code: 'hu', name: 'Magyar' }, { code: 'en', name: 'English' }] },
  { code: 'sk', name: 'Słowacja', domain: 'wecotravel.sk', defaultLocale: 'sk', locales: [{ code: 'sk', name: 'Slovenčina' }, { code: 'en', name: 'English' }] }
];

const normaliseHost = (host: string) => host.toLowerCase().split(':')[0].replace(/\.$/, '');

/**
 * Does `host` sit on this domain, or under it?
 *
 * `wecotravel.pl` itself, `www.wecotravel.pl`, `beta.wecotravel.pl` - all Poland. The
 * leading dot in the suffix is what makes it a *boundary* rather than a string match:
 * without it `notwecotravel.pl` would also be Poland.
 *
 * **This replaced an exact match against the domain with `www.` stripped**, which is a
 * narrower rule than it looks: it answers only for the two spellings somebody thought of.
 * `beta.wecotravel.cz` was added to the chart's host list and, under that rule, named no
 * region at all - so `resolveRegion` fell through to `regions[0]` and every market's beta
 * site served Polish content. Nothing errors when that happens, which is why the rule is
 * now about the whole subtree.
 */
const isUnder = (host: string, domain: string) => {
  const owner = normaliseHost(domain);
  return Boolean(owner) && (host === owner || host.endsWith(`.${owner}`));
};

/**
 * The region this host belongs to by domain, and the domain that claimed it.
 *
 * The longest match wins. No region's domain is a suffix of another's today - they are
 * five different TLDs - but a market added later on a subdomain of an existing one would
 * otherwise be answered for by its parent, quietly.
 */
function domainOwner(regions: Region[], host: string): { region: Region; domain: string } | null {
  let best: { region: Region; domain: string } | null = null;
  for (const region of regions) {
    for (const candidate of [region.domain, ...(region.extraDomains || [])]) {
      if (!isUnder(host, candidate)) continue;
      const domain = normaliseHost(candidate);
      if (!best || domain.length > best.domain.length) best = { region, domain };
    }
  }
  return best;
}

/**
 * The market a non-production host names, and the code that matched.
 *
 * The code is at the **end of the first hyphen-separated segment of the first label**:
 * `wecotravelpl-test.dc.weco.pl` on test, `pl.localhost` on a laptop. Both give `pl`,
 * because a segment that *is* the code ends with it too.
 *
 * **It was an exact match until 2026-09-13**, when the test hosts became
 * `wecotravel<market>-test`. Under the old rule the first segment was `wecotravelpl`,
 * which is not a code, so all five fell through to `regions[0]`: the Czech, Romanian,
 * Hungarian and Slovak test sites would every one of them have served Polish content, with
 * nothing anywhere to say so. That is the third naming change this rule has survived, and
 * every one of them failed in the same silent direction.
 *
 * Matching a suffix is looser than matching the whole segment, and the cost is worth
 * stating: any first segment ending in two letters that happen to be a market code
 * resolves to that market. It only matters for hosts that reach this site at all, and the
 * ones that do are in the chart.
 *
 * The code is returned alongside the region because `regionSwitchUrl` has to replace
 * exactly those characters - swapping the whole segment would turn
 * `wecotravelpl-test` into `cz-test`.
 *
 * `apps/cms/src/api/website/controllers/website.js` has to agree with this exactly - the
 * two disagreeing means a visitor reads one market while `sitemap.xml` and `robots.txt`
 * answer for another. `pnpm verify` check [18] holds the deployed hosts to the same rule
 * and `pnpm check:regions` runs this function against them.
 */
function labelRegion(regions: Region[], host: string): { region: Region; code: string } | null {
  const segment = host.split('.')[0].split('-')[0];
  let best: { region: Region; code: string } | null = null;
  for (const region of regions) {
    const code = String(region.code || '').toLowerCase();
    if (!code || !segment.endsWith(code)) continue;
    // Longest wins, so a two-letter code cannot answer for a longer one that ends in it.
    if (!best || code.length > best.code.length) best = { region, code };
  }
  return best;
}

/** Match a hostname against a region's domain, including its extra domains. */
export function resolveRegion(regions: Region[], hostname: string, forced?: string): Region {
  if (forced) {
    const byCode = regions.find((r) => r.code === forced);
    if (byCode) return byCode;
  }

  const host = normaliseHost(hostname);
  const owner = domainOwner(regions, host);
  if (owner) return owner.region;

  // Non-production hosts carry the market at the end of the first segment of the first
  // label: `wecotravelpl-test.dc.weco.pl` on test, `pl.localhost` on a laptop. One rule for
  // both, so there is nothing to keep in step. See `labelRegion` for what it has survived.
  const byLabel = labelRegion(regions, host);
  if (byLabel) return byLabel.region;

  return regions[0];
}

export function isKnownLocale(region: Region, locale: string): boolean {
  return region.locales.some((l) => l.code === locale);
}

/**
 * Does this hostname belong to a region in its own right - a production domain or one of
 * its aliases?
 *
 * It gates the `?region=` override below. On `wecotravel.pl` the region is settled by the
 * domain and a query parameter must not be able to change it: that would put Czech
 * content on a Polish address, which is a thing to be crawled and indexed. On a host that
 * is nobody's domain - `localhost`, a preview URL - there is no domain to respect, and
 * the parameter is the only way to look at another region at all.
 */
export function isRegionDomain(regions: Region[], hostname: string): boolean {
  return domainOwner(regions, normaliseHost(hostname)) !== null;
}

/**
 * Where "switch to this region" should take the visitor.
 *
 * Regions are separate sites on separate domains, so switching is a navigation, not a
 * state change - and where to navigate depends on how the current address names its
 * region in the first place. There are three cases, and the switcher is unusable if any
 * of them is missed:
 *
 * | Current host | Example | Target |
 * |---|---|---|
 * | a region's domain, or anything under it | `wecotravel.pl`, `beta.wecotravel.pl` | the same prefix on the target's domain, `https://wecotravel.cz/cs/`, `https://beta.wecotravel.cz/cs/` |
 * | the market at the end of the first segment | `pl.localhost:8080`, `wecotravelpl-test.dc.weco.pl` | the same host with those characters swapped, `cz.localhost:8080/cs/`, `wecotravelcz-test.dc.weco.pl` |
 * | neither | `localhost:5174` | this origin with `?region=cz` |
 *
 * The middle case is what the k3d cluster and the test environment both use, and the last is the dev
 * server, where `wecotravel.cz` resolves to nothing anybody is running.
 *
 * The path is always the target region's default language at the site root. Slugs are
 * per region - they are not translations of each other - so the page you are on has no
 * counterpart to land on.
 */
export function regionSwitchUrl(
  regions: Region[],
  target: Region,
  location: { hostname: string; protocol: string; port: string; origin: string }
): string {
  const path = `/${target.defaultLocale}/`;
  const host = normaliseHost(location.hostname);
  const port = location.port ? `:${location.port}` : '';

  // On a region's own domain, only the domain changes: `beta.wecotravel.pl` switches to
  // `beta.wecotravel.cz` and `www.` to `www.`, so a visitor looking at a beta site stays on
  // the beta site. Dropping the prefix would quietly move somebody from beta to production.
  const owner = domainOwner(regions, host);
  if (owner) {
    const prefix = host === owner.domain ? '' : host.slice(0, host.length - owner.domain.length);
    return `https://${prefix}${target.domain}${path}`;
  }

  // Swap **the characters the code matched**, not the whole segment. `pl.localhost` becomes
  // `cz.localhost`; `wecotravelpl-test.dc.weco.pl` becomes `wecotravelcz-test.dc.weco.pl`.
  //
  // Replacing the segment would give `cz-test.dc.weco.pl`, a host nobody serves. Replacing
  // everything up to the first dot - which it did until 2026-09-10 - stopped matching the
  // moment the test hosts gained a hyphen, and the switcher fell through to the `?region=`
  // branch below: the visitor stayed on the Polish address reading Czech content. Working,
  // in the sense that does the most damage.
  const named = labelRegion(regions, host);
  if (named) {
    const [label, ...rest] = host.split('.');
    const segments = label.split('-');
    segments[0] = segments[0].slice(0, segments[0].length - named.code.length) + target.code;
    return `${location.protocol}//${[segments.join('-'), ...rest].join('.')}${port}${path}`;
  }

  return `${location.origin}${path}?region=${encodeURIComponent(target.code)}`;
}

const OVERRIDE_KEY = 'weco.region';

/**
 * The region override for hosts that are nobody's domain, and only those.
 *
 * `?region=cz` is what the switcher puts on a dev or preview URL. It is remembered for
 * the tab because it would otherwise last exactly one page: internal links are router
 * links and do not carry the query, so the first click through the menu, or any reload,
 * would drop the visitor back into Poland - which reads as the switcher not having
 * worked. `sessionStorage`, not `localStorage`: a region chosen to look at something is
 * not a preference to keep until the browser is reinstalled.
 *
 * On a real regional domain this returns nothing at all, whatever is in the query or
 * left over in storage. There the domain is the answer.
 */
export function readRegionOverride(regions: Region[], location: { hostname: string; search: string }): string {
  if (isRegionDomain(regions, location.hostname)) return '';

  const fromQuery = new URLSearchParams(location.search).get('region') || '';
  try {
    if (fromQuery) {
      sessionStorage.setItem(OVERRIDE_KEY, fromQuery);
      return fromQuery;
    }
    return sessionStorage.getItem(OVERRIDE_KEY) || '';
  } catch {
    // Private mode, or storage switched off. The query still works for this page.
    return fromQuery;
  }
}
